import React from "react";
import { Sparkles, Heart, Shield, Award } from "lucide-react";
import { useNavigate } from "react-router-dom";

const gradientClass =
  "bg-gradient-to-b from-[#484139] via-[#544C44] via-[#5D554C] via-[#655E54] to-[#6B5B4F]";

const About = () => {
  const navigate = useNavigate();

  return (
    <div className={`about-page min-h-screen ${gradientClass}`}>
      {/* Hero */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center text-white">
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-serif font-bold mb-4 sm:mb-6">
            About <span className="text-[#edd9c0]">Glowy</span>
          </h1>
          <p className="text-base sm:text-xl lg:text-2xl text-[#E3D5C5] max-w-3xl mx-auto leading-relaxed">
            Your trusted destination for discovering and purchasing the best skincare
            products from the world's most famous brands.
          </p>
        </div>
      </section>

      {/* Our Ethos */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
            <div className="space-y-5 sm:space-y-6 order-2 lg:order-1">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-[#1a1918]">
                Our <span className="text-[#d6c6b2]">Ethos</span>
              </h2>
              <p className="text-base sm:text-lg text-[#f2e9df] leading-relaxed">
                We believe achieving truly radiant skin is a journey of self-care and
                confidence. Our mission is to make that journey effortless and joyful by
                curating a diverse selection of high-quality products that deliver
                authenticity, freshness, natural hydration, and a sense of profound
                indulgence.
              </p>
              <div className="flex items-center gap-3 pt-2">
                <Heart className="w-5 h-5 sm:w-6 sm:h-6 text-[#c7bfb5]" />
                <span className="text-[#c7bfb5] font-semibold text-sm sm:text-base">
                  Crafted with love and care
                </span>
              </div>
            </div>
            <div className="rounded-lg overflow-hidden order-1 lg:order-2">
              <img
                src="/Skincare routine.jpg"
                alt="Skincare routine"
                className="w-full h-56 sm:h-72 lg:h-80 object-cover rounded-lg"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Why Choose Glowy */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10 sm:mb-14 lg:mb-16">
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-[#1a1918] mb-4 sm:mb-6">
              Why Choose <span className="text-[#d6c6b2]">Glowy?</span>
            </h2>
            <p className="text-base sm:text-lg text-[#f2e9df] max-w-4xl mx-auto leading-relaxed">
              We combine meticulous research and contemporary beauty trends with practical
              experience to ensure every item in our collection is a step toward your best
              skin yet.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 sm:gap-6 lg:gap-8">
            {[
              {
                Icon: Shield,
                title: "Authenticity",
                body:
                  "Every product is sourced directly from authorized distributors, ensuring 100% authentic skincare solutions.",
              },
              {
                Icon: Sparkles,
                title: "Freshness",
                body:
                  "Our products are stored in optimal conditions and have the latest manufacturing dates for maximum efficacy.",
              },
              {
                Icon: Heart,
                title: "Natural Hydration",
                body:
                  "Curated selection focuses on natural ingredients that provide deep, lasting hydration for all skin types.",
              },
              {
                Icon: Award,
                title: "Indulgence",
                body:
                  "Transform your daily routine into a luxurious self-care ritual with our premium product selection.",
              },
            ].map(({ Icon, title, body }) => (
              <div
                key={title}
                className="bg-white/80 backdrop-blur-sm rounded-xl p-6 sm:p-8 shadow-2xl transition-all duration-300 hover:shadow-3xl hover:scale-[1.02] text-center border border-white/50"
              >
                <div className="bg-[#484139]/30 backdrop-blur-lg rounded-full p-4 w-14 h-14 sm:w-16 sm:h-16 mx-auto mb-4 sm:mb-6 flex items-center justify-center">
                  <Icon className="w-7 h-7 sm:w-8 sm:h-8 text-[#484139]" />
                </div>
                <h3 className="text-lg sm:text-xl font-serif font-bold text-[#484139] mb-3 sm:mb-4">
                  {title}
                </h3>
                <p className="text-sm sm:text-base text-gray-700 leading-relaxed">{body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-serif font-bold text-white mb-4 sm:mb-6">
            Ready to discover your next glow-up?
          </h2>
          <p className="text-base sm:text-xl text-white/90 mb-7 sm:mb-8 max-w-2xl mx-auto">
            Join thousands of satisfied customers who have transformed their skincare
            routine with Glowy's curated collection.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 sm:gap-4 justify-center">
            <button
              onClick={() => navigate("/products")}
              className="bg-white text-[#8B7355] px-7 sm:px-8 py-3 sm:py-4 rounded-full font-serif font-semibold text-base sm:text-lg hover:bg-gray-100 transition flex items-center justify-center"
            >
              Shop Now
            </button>
            <button
              onClick={() => navigate("/products")}
              className="border-2 border-white text-white px-7 sm:px-8 py-3 sm:py-4 rounded-full font-serif font-semibold text-base sm:text-lg hover:bg-white hover:text-[#8B7355] transition"
            >
              Explore Products
            </button>
          </div>
        </div>
      </section>

      {/* Brand Values */}
      <section className="py-12 sm:py-16 lg:py-20">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 sm:gap-10 lg:gap-12">
            {[
              {
                img: "/Global brands.webp",
                title: "Global Brands",
                body:
                  "Partnering with world-renowned skincare brands to bring you the finest products from around the globe.",
              },
              {
                img: "/Expert curation.webp",
                title: "Expert Curation",
                body:
                  "Our skincare experts carefully select each product based on efficacy, quality, and customer satisfaction.",
              },
              {
                img: "/Customer care.jpeg",
                title: "Customer Care",
                body:
                  "Dedicated support team ready to help you find the perfect products for your unique skincare needs.",
              },
            ].map((card) => (
              <div key={card.title} className="text-center">
                <div className="bg-white/10 backdrop-blur-sm rounded-2xl mb-4 sm:mb-6 border border-white/20 overflow-hidden aspect-[4/3]">
                  <img
                    src={card.img}
                    alt={card.title}
                    className="w-full h-full object-cover rounded-2xl"
                  />
                </div>
                <h3 className="text-xl sm:text-2xl font-serif font-bold text-white mb-3 sm:mb-4">
                  {card.title}
                </h3>
                <p className="text-sm sm:text-base text-[#f2e9df] leading-relaxed max-w-xs mx-auto">
                  {card.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
